/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useEffect, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { userIsChannelBasedSelector } from '@Store/api/auth'
import {
  useCancelImportMutation,
  useCreateImportMutation,
} from '@Store/api/product'
import { useAppSelector } from '@Store/hooks'
import type { ColumnMapping, SelectedColumn } from '@Store/types/product'

import CancelImportModal from '../components/file-import/CancelImportModal'
import ImportMapFields from '../components/file-import/ImportMapFields'
import useMatchFieldsReducer, {
  type MatchFieldsActions,
  type MatchFieldsState,
} from '../hooks/useMatchFieldsReducer'

export type BatchImportState = {
  queueId?: number
  uploadId?: number
  isChannelBased?: boolean
  cancelClicked?: boolean
  isCreateImportLoading?: boolean
  isCancelImportLoading?: boolean
} & Partial<MatchFieldsState>

export type BatchImportDispatch = {
  setQueueId: Dispatch<SetStateAction<BatchImportState['queueId']>>
  setUploadId: Dispatch<SetStateAction<BatchImportState['uploadId']>>
  cancelImport: () => void
  createImport: () => void
  setCancelClicked: Dispatch<SetStateAction<BatchImportState['cancelClicked']>>
  // The provider always supplies these from useMatchFieldsReducer, so they are
  // not optional. Declaring the whole set Partial only forced consumers into
  // non-null assertions, which is exactly the check worth keeping. setOptions
  // and clearState stay out because the provider genuinely does not pass them.
} & Pick<
  MatchFieldsActions,
  | 'setMode'
  | 'setMatchData'
  | 'setMatchedSelected'
  | 'removeMatchedSelected'
  | 'setUnmatchedSelected'
  | 'removeUnmatchedSelected'
>

export const BatchImportStateContext = createContext<BatchImportState>({})
BatchImportStateContext.displayName = 'BatchImportStateContext'

export const BatchImportDispatchContext = createContext<BatchImportDispatch>(
  {} as BatchImportDispatch,
)
BatchImportDispatchContext.displayName = 'BatchImportDispatchContext'

function BatchImportProvider({
  children,
  initialMode,
}: {
  children: React.ReactNode
  initialMode: BatchImportState['mode']
}): JSX.Element {
  const isChannelBased = useAppSelector(userIsChannelBasedSelector)

  const {
    state: {
      mode,
      options,
      matchedProperties,
      unmatchedProperties,
      matchedSelected,
      unmatchedSelected,
      allSelected,
      requiredNotSelected,
    },
    actions: {
      setMode,
      setOptions,
      setMatchData,
      setMatchedSelected,
      removeMatchedSelected,
      setUnmatchedSelected,
      removeUnmatchedSelected,
      clearState: clearMatchSelected,
    },
  } = useMatchFieldsReducer()

  const [queueId, setQueueId] = useState<BatchImportState['queueId']>()
  const [uploadId, setUploadId] = useState<BatchImportState['uploadId']>()
  const [cancelClicked, setCancelClicked] =
    useState<BatchImportState['cancelClicked']>(false)

  const [
    createImportMutation,
    {
      data: createImportData,
      isUninitialized: isCreateImportUninitialized,
      isSuccess: isCreateImportSuccess,
      isLoading: isCreateImportLoading,
      isError: isCreateImportError,
      error: createImportError,
    },
  ] = useCreateImportMutation()

  const [
    cancelImportMutation,
    {
      isSuccess: isCancelImportSuccess,
      isLoading: isCancelImportLoading,
      isError: isCancelImportError,
      error: cancelImportError,
    },
  ] = useCancelImportMutation()

  const clearState = (): void => {
    clearMatchSelected()
    setQueueId(undefined)
    setUploadId(undefined)
    setCancelClicked(false)
    setMode(initialMode)
  }

  const cancelImport = async (): Promise<void> => {
    if (queueId) {
      await cancelImportMutation(queueId)
    } else {
      await clearState()
    }
  }

  const createImport = async (): Promise<void> => {
    if (!uploadId || !mode) {
      return
    }

    const selectedFields = (allSelected ?? []).reduce(
      (acc: ColumnMapping[], cur: SelectedColumn) => {
        if (cur.isSelected) {
          acc.push({
            index: cur.index,
            value: cur.key,
          })
        }
        return [...acc]
      },
      [],
    )

    await createImportMutation({
      upload_id: uploadId,
      upload_type: mode as 'apt' | 'upt' | 'upt-c',
      column_mappings: selectedFields,
      event_source: 'panel',
      find_marketplace_competitors: false,
    })
  }

  const handleCancelImportModalDismiss = (): void => {
    setCancelClicked(false)
  }

  const handleCancelImportModalConfirm = async (): Promise<void> => {
    await cancelImport()
  }

  useEffect(() => {
    if (isCreateImportSuccess && createImportData?.status) {
      clearState()
    }
  }, [createImportData, isCreateImportSuccess])

  useEffect(() => {
    if (isCancelImportSuccess) {
      clearState()
    }
  }, [isCancelImportSuccess])

  useEffect(() => {
    setMode(initialMode)
  }, [initialMode])

  return (
    <BatchImportStateContext.Provider
      value={{
        mode,
        queueId,
        uploadId,
        options,
        matchedProperties,
        unmatchedProperties,
        matchedSelected,
        unmatchedSelected,
        allSelected,
        requiredNotSelected,
        isChannelBased,
        cancelClicked,
        isCreateImportLoading,
        isCancelImportLoading,
      }}
    >
      <BatchImportDispatchContext.Provider
        value={{
          setMode,
          setMatchData,
          setMatchedSelected,
          removeMatchedSelected,
          setUnmatchedSelected,
          removeUnmatchedSelected,
          setQueueId,
          setUploadId,
          cancelImport,
          createImport,
          setCancelClicked,
        }}
      >
        <>{children}</>
        {uploadId && <ImportMapFields />}
        <CancelImportModal
          onModalClose={handleCancelImportModalDismiss}
          onModalConfirm={handleCancelImportModalConfirm}
          isCancelImportLoading={isCancelImportLoading}
          cancelClicked={cancelClicked}
        />
      </BatchImportDispatchContext.Provider>
    </BatchImportStateContext.Provider>
  )
}

export default BatchImportProvider
